import { zodResolver } from '@hookform/resolvers/zod';
import { CodeXml, Download, Eye, Loader2, Minus, Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { useStrategyGenerateEmailTemplate } from '@/hooks/useStrategyGenerateEmailTemplate';
import i18n from '@/lib/i18n';
import { Audience } from '@/services/AudiencesService';

const formSchema = z.object({
  template: z.string().min(1, {
    message: i18n.t('pages.Behaviors.customBehavior.emailTemplateFormError'),
  }),
});

type FormData = z.infer<typeof formSchema>;

interface EmailTemplateProps {
  audience: Pick<Audience, 'id' | 'emailTemplate'>;
}

export function EmailTemplate({ audience }: EmailTemplateProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Behaviors.customBehavior',
  });

  const {
    formState: { isSubmitting },
    register,
    reset,
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const { generateEmailTemplate } = useStrategyGenerateEmailTemplate();

  const onSubmit = handleSubmit(async (data) => {
    try {
      await generateEmailTemplate({
        audienceId: audience.id,
        template: data.template,
      });

      reset();
      setIsExpanded(false);

      toast.success(t('emailTemplateFormToastSuccess'));
    } catch {
      toast.error(t('emailTemplateFormToastError'));
    }
  });

  function handleToggleModal() {
    setIsModalOpen((value) => !value);
  }

  function handleToggleExpanded() {
    setIsExpanded((value) => !value);
  }

  function handleDownload() {
    const htmlString = audience.emailTemplate;

    if (!htmlString) {
      return;
    }

    const blob = new Blob([htmlString], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${uuidv4()}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  return (
    <>
      <div className="flex flex-row justify-between gap-2">
        <p className="text-sm text-gray-600">{t('emailTemplateLabel')}</p>

        <div className="text-w-10 flex flex-row items-center gap-2 font-semibold text-blue-600 hover:underline">
          <CodeXml className="h-4 w-4" />

          <button onClick={handleToggleModal}>
            <Eye className="h-4 w-4" />
          </button>
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-h-[80vh] sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t('emailTemplateTitle')}</DialogTitle>

            <DialogDescription />
          </DialogHeader>

          <div className="space-y-4">
            {audience.emailTemplate && (
              <Button
                onClick={handleDownload}
                className="w-full justify-start bg-transparent"
                variant="outline"
              >
                <Download className="mr-2 h-4 w-4" />
                {t('emailTemplateDownloadButtonLabel')}
              </Button>
            )}

            {!audience.emailTemplate && (
              <p className="text-sm text-muted-foreground">
                {t('emailTemplateFallback')}
              </p>
            )}

            <Button
              className="w-full justify-start bg-transparent"
              variant="outline"
              onClick={handleToggleExpanded}
            >
              {isExpanded ? (
                <Minus className="mr-2 h-4 w-4" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              {t('emailTemplateGenerateNewButtonLabel')}
            </Button>
          </div>

          {isExpanded && (
            <form className="space-y-4 py-4" onSubmit={onSubmit}>
              <FormGroup className="space-y-2">
                <label htmlFor="html-template" className="text-sm font-medium">
                  {t('emailTemplateFormTitle')}
                </label>

                <textarea
                  id="html-template"
                  placeholder={t('emailTemplateFormPlaceholder')}
                  className="min-h-[400px] w-full resize-none rounded-md border border-input bg-background px-3 py-2 font-mono text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  readOnly={isSubmitting}
                  {...register('template')}
                />
              </FormGroup>

              <div className="mb-4 flex gap-2">
                <Button
                  disabled={isSubmitting}
                  type="submit"
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    </>
                  ) : (
                    t('emailTemplateFormSubmitButtonLabel')
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
